import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {FeedbackButtonProps} from './feedback-button.types';
import {setUserCompletedSurvey, shouldShowUserFeedbackSurvey} from './feedback-button.types';
import styles from './feedback-button.module.scss';
import './feedback-button.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';

export function FeedbackButton({
  id,
  floatingFeedbackButtonProps = undefined,
  onClick = undefined,
  isStickingToRight = false,
  uriToShowSurveyButton,
  uriToMarkSurveyAsCompleted,
  btnText = '',
  btnCustomCLasses = '',
}: FeedbackButtonProps): ReactElement<any> | undefined {
  const [shouldShowFeedbackButton, setShouldShowFeedbackButton] = useState<boolean>(false);
  const [isHotjarFormVisible, setIsHotjarFormVisible] = useState<boolean>(false);
  const hotJarHTMLContainerRef = useRef<HTMLDivElement>(null);
  const shouldRenderNothing = !shouldShowFeedbackButton;

  const markUserCompletedSurvey = (): void => {
    setShouldShowFeedbackButton(false);
    void setUserCompletedSurvey(uriToMarkSurveyAsCompleted);
  };

  useEffect(() => {
    void shouldShowUserFeedbackSurvey(uriToShowSurveyButton).then((shouldShow) => {
      setShouldShowFeedbackButton(shouldShow);
    });
  }, [uriToShowSurveyButton]);

  useEffect(() => {
    if (!hotJarHTMLContainerRef.current) {
      return;
    }

    const TARGET_CLASS_NAME = 'css-1owpeli';

    const onDOMModified = (mutationsList: MutationRecord[], observer: MutationObserver): void => {
      for (const mutation of mutationsList) {
        if (mutation.type !== 'childList') {
          continue;
        }

        let endPanel = hotJarHTMLContainerRef.current?.querySelector(`.${TARGET_CLASS_NAME}`);
        endPanel = endPanel ?? hotJarHTMLContainerRef.current?.querySelector('h2');

        if (!endPanel?.textContent?.toLowerCase()?.includes('thank you for helping us')) {
          continue;
        }

        setTimeout(() => {
          markUserCompletedSurvey();
        }, 3000);
        observer.disconnect();
        return;
      }
    };

    const observer = new MutationObserver(onDOMModified);
    const config = {childList: true, subtree: true};
    observer.observe(hotJarHTMLContainerRef.current, config);

    return (): void => {
      observer.disconnect();
    };
  }, [id, shouldRenderNothing]);

  const closeSurvey = (e: React.MouseEvent): void => {
    e.stopPropagation();
    markUserCompletedSurvey();
  };

  const onClickBtn = (): void => {
    setIsHotjarFormVisible(!isHotjarFormVisible);

    if (onClick) {
      onClick();
    }
  };

  const getButton = (): ReactElement<any> => {
    return (
      <>
        <div className="flex-row-align-center _position-relative _full-width">
          <ClickableDiv
            onClick={onClickBtn}
            className={`${styles.buttonContainer} ${id ? styles.hotjarSelector : ''}  ${btnCustomCLasses} _cursor-pointer flex-row-align-center spacing-p-l-4`}
          >
            <Icon className="spacing-p-0 content-body-white" icon="icon-chat" size={IconSize.SIZE_ICON_16} type={IconType.TRANSPARENT} />
            <Text className="flex-row-align-center content-body-white" val={btnText} size={TextSize.XSMALL} bold />
          </ClickableDiv>
          <Icon
            isUsedAsButton
            onClick={closeSurvey}
            className={`${styles.crossIcon} position-absolute spacing-p-2 content-body-white`}
            icon="icon-close"
            size={IconSize.SIZE_ICON_16}
            type={IconType.DARK_GHOST}
            shape={IconShape.SQUARE}
          />
        </div>
        {id ? getHotjarContent() : undefined}
      </>
    );
  };

  const getHotjarContent = (): ReactElement<any> => {
    return (
      <div className={`${styles.hotjarContainer} _overflow-auto ${isHotjarFormVisible ? styles.hotjarVisible : ''}`}>
        <div ref={hotJarHTMLContainerRef} id={id}></div>
      </div>
    );
  };

  const getFloatingButton = (): ReactElement<any> => {
    const style: React.CSSProperties = {
      ...(floatingFeedbackButtonProps?.top !== undefined ? {top: floatingFeedbackButtonProps.top} : {}),
      ...(floatingFeedbackButtonProps?.bottom !== undefined ? {bottom: floatingFeedbackButtonProps.bottom} : {}),
      ...(floatingFeedbackButtonProps?.right !== undefined ? {right: floatingFeedbackButtonProps.right} : {}),
      ...(floatingFeedbackButtonProps?.left !== undefined ? {left: floatingFeedbackButtonProps.left} : {}),
    };

    return (
      <div className={`${styles.floatingContainer} flex-column-align-start`} style={style}>
        {getButton()}
      </div>
    );
  };

  if (shouldRenderNothing) {
    return undefined;
  }

  if (floatingFeedbackButtonProps) {
    return getFloatingButton();
  }

  if (isStickingToRight) {
    return <div className={`${styles.floatingContainer} ${styles.rightStuck} flex-column-align-start`}>{getButton()}</div>;
  }
  return getButton();
}
